import {
  ApiErrorInstance,
  isApi5xxResponseStatus,
  withHandleError
} from '~/api';
import { HttpStatusCode } from 'axios';
import { getLocalizedString } from '~/i18n/utils.ts';
import { isFeatureEnabled } from '~/utils/features/featureFlag';
import { FeatureFlagKeys } from '~/api/types/featureFlagData';
import {
  ApplicationCustomizationSettings,
  BorderRadiusOptions,
  CustomizationSettings,
  HorizontalAlignmentOptions,
  LogoPositionOptions,
  PortalCustomizationSettings,
  VerticalAlignmentOptions
} from '~/api/types/customisationData';
import { cloneObject, getAppBaseUrl } from '~/utils/commonUtils.ts';
import {
  getSlug,
  HostedAuthPaths,
  replacePathParams
} from '~/components/AppRouter/utils.ts';

const TRANSLATION_BASE_PATH = `pages.customization`;

const DEFAULT_ORGANIZATION_META_NAME = 'Organization';

const getTranslationKey = (translationStr: string): string =>
  `${TRANSLATION_BASE_PATH}.${translationStr}`;

const DEFAULT_COLORS = {
  buttonColor: '#000000',
  textColor: '#ffffff',
  bgColor: '#f0f0f0'
};

const BORDER_RADIUS_CUSTOMIZATION_CONFIG: Partial<
  Record<
    BorderRadiusOptions,
    {
      label: string;
    }
  >
> = {
  [BorderRadiusOptions.NONE]: {
    label: 'None'
  },

  [BorderRadiusOptions.SM]: {
    label: 'Small'
  },
  [BorderRadiusOptions.MD]: {
    label: 'Medium'
  },
  [BorderRadiusOptions.LG]: {
    label: 'Large'
  }
};

const CUSTOM_CSS_DEFAULT_VALUE = ``;

const getHoverColor = (color: string, alpha: number): string => {
  if (!color.includes(`rgba`)) {
    return color;
  }
  const rgba = color.replace(/^rgba?\(|\s+|\)$/g, '').split(',');
  rgba[3] = String(parseInt(rgba[3]) - alpha);
  return `rgba(${rgba.join(',')})`;
};

const errorTranslationBasePath = 'server_errors.customizations';

const handleUpdateCustomizationError = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  if (
    serverError.httpStatus === HttpStatusCode.BadRequest ||
    isApi5xxResponseStatus(serverError.httpStatus)
  ) {
    return getLocalizedString(`${errorTranslationBasePath}.update_failed`, {
      intl
    });
  }
});

const getDefaultPortalCustomizationSettings =
  (): PortalCustomizationSettings => {
    return {
      logoUrl: '',
      iconUrl: '',
      buttonColor: DEFAULT_COLORS.buttonColor,
      textColor: DEFAULT_COLORS.textColor,
      loginBgColor: DEFAULT_COLORS.bgColor,
      ...(isFeatureEnabled(
        FeatureFlagKeys.PORTAL_CUSTOMIZATION_DISALLOW_DISABLE_CONNECTION
      ) && { disallowDisableEnabledConnection: false }),
      logoPosition: LogoPositionOptions.INSIDE_BOX,
      logoAlignment: HorizontalAlignmentOptions.CENTER,
      headerTextAlignment: HorizontalAlignmentOptions.CENTER,
      socialPlacement: VerticalAlignmentOptions.TOP,
      borderRadius: BorderRadiusOptions.MD,
      pageBackgroundCss: CUSTOM_CSS_DEFAULT_VALUE
    };
  };

const getDefaultApplicationCustomizationSettings =
  (): ApplicationCustomizationSettings => {
    return {
      name: '',
      supportEmail: '',
      privacyPolicyUrl: '',
      termsOfServiceUrl: '',
      organizationMetaName: DEFAULT_ORGANIZATION_META_NAME
    };
  };

const getCustomizationSettingsWithDefaults = (
  customizationSettings: CustomizationSettings
) => {
  return {
    applicationCustomization: {
      ...getDefaultApplicationCustomizationSettings(),
      ...(customizationSettings?.applicationCustomization ?? {})
    },
    portalCustomization: {
      ...getDefaultPortalCustomizationSettings(),
      ...(customizationSettings?.portalCustomization ?? {})
    }
  } as CustomizationSettings;
};

enum CustomizationTabTypes {
  BRANDING_AND_THEMING = 'branding',
  CUSTOM_DOMAIN = 'custom-domain',
  APPLICATION_SETTINGS = 'application-settings'
}

enum CustomizationWorkflowTypes {
  LOGIN = 'login',
  SIGNUP = 'signup',
  EMAILS = 'emails',
  ADMIN_PORTAL = 'admin-portal'
}

enum CustomizationPageTypes {
  LOGIN_BOX = 'login-box',
  LOGIN_BOX_WITH_LINK_SUCCESS = 'login-box-link-success',
  LOGIN_BOX_OTP = 'login-box-otp',
  LOGIN_BOX_WITH_LINK_OTP = 'login-box-link-otp-success',
  ADMIN_PORTAL_PROVIDER_SELECTION = 'admin-portal-provider-selection'
}

export type CustomizationPageTypeDisplaySettings = {
  displayNameTranslationKey: string;
  disabled?: boolean;
};

const CUSTOMIZATION_PAGE_TYPE_DISPLAY_SETTINGS: Partial<
  Record<CustomizationPageTypes, CustomizationPageTypeDisplaySettings>
> = {
  [CustomizationPageTypes.LOGIN_BOX]: {
    displayNameTranslationKey: 'login'
  },
  [CustomizationPageTypes.LOGIN_BOX_WITH_LINK_OTP]: {
    displayNameTranslationKey: 'login_link_code'
  },
  [CustomizationPageTypes.LOGIN_BOX_OTP]: {
    displayNameTranslationKey: 'verification_code'
  },
  [CustomizationPageTypes.LOGIN_BOX_WITH_LINK_SUCCESS]: {
    displayNameTranslationKey: 'login_link_success'
  },
  [CustomizationPageTypes.ADMIN_PORTAL_PROVIDER_SELECTION]: {
    displayNameTranslationKey: 'admin_portal'
  }
};

const CUSTOMIZATION_WORKFLOW_TYPE_CONFIG: Partial<
  Record<
    CustomizationWorkflowTypes,
    {
      pageTypes: CustomizationPageTypes[];
      enabled: boolean;
    }
  >
> = {
  [CustomizationWorkflowTypes.LOGIN]: {
    pageTypes: [
      CustomizationPageTypes.LOGIN_BOX,
      CustomizationPageTypes.LOGIN_BOX_WITH_LINK_SUCCESS,
      CustomizationPageTypes.LOGIN_BOX_OTP,
      CustomizationPageTypes.LOGIN_BOX_WITH_LINK_OTP
    ],
    enabled: true
  },
  [CustomizationWorkflowTypes.ADMIN_PORTAL]: {
    pageTypes: [CustomizationPageTypes.ADMIN_PORTAL_PROVIDER_SELECTION],
    enabled: true
  }
};

type WorkflowDisplaySettingType = {
  type: CustomizationWorkflowTypes;
  displayNameTranslationKey: string;
  enabled: boolean;
};

const loginWorkflow: WorkflowDisplaySettingType = {
  type: CustomizationWorkflowTypes.LOGIN,
  displayNameTranslationKey: 'login',
  enabled: true
};

const adminPortalWorkflow: WorkflowDisplaySettingType = {
  type: CustomizationWorkflowTypes.ADMIN_PORTAL,
  displayNameTranslationKey: 'admin_portal',
  enabled: true
};

const otherWorkflows: WorkflowDisplaySettingType[] = [
  {
    type: CustomizationWorkflowTypes.SIGNUP,
    displayNameTranslationKey: 'signup',
    enabled: false
  },
  {
    type: CustomizationWorkflowTypes.EMAILS,
    displayNameTranslationKey: 'emails',
    enabled: false
  }
];

const getWorkflowDisplaySettings = (): WorkflowDisplaySettingType[] =>
  isFeatureEnabled(FeatureFlagKeys.FULL_STACK_AUTHENTICATION)
    ? [loginWorkflow, adminPortalWorkflow, ...otherWorkflows]
    : [adminPortalWorkflow, loginWorkflow, ...otherWorkflows];

const getPreviewUrl = ({
  workflowType,
  pageType,
  previewId
}: {
  workflowType: CustomizationWorkflowTypes;
  pageType: CustomizationPageTypes;
  previewId: string;
}): string => {
  const baseUrl = getAppBaseUrl();
  if (workflowType === CustomizationWorkflowTypes.LOGIN) {
    return `${baseUrl}${getSlug.hostedAuthPages(
      replacePathParams(HostedAuthPaths.LOGIN_PREVIEW, {
        previewId
      }),
      true
    )}?previewType=${pageType}`;
  } else if (workflowType === CustomizationWorkflowTypes.ADMIN_PORTAL) {
    return `${baseUrl}${getSlug.hostedAuthPages(
      replacePathParams(HostedAuthPaths.ADMIN_PORTAL_PREVIEW, {
        previewId
      }),
      true
    )}?previewType=${pageType}`;
  }
  return '';
};

const getLogoContainerClass = (
  portalCustomization: PortalCustomizationSettings
) => {
  const classes: string[] = [];
  switch (portalCustomization.logoAlignment) {
    case HorizontalAlignmentOptions.LEFT:
      classes.push('self-start');
      break;
    case HorizontalAlignmentOptions.CENTER:
      classes.push('self-center');
      break;
    case HorizontalAlignmentOptions.RIGHT:
      classes.push('self-end');
      break;
  }
  return classes.join(' ');
};

const getHeaderTextContainerClass = (
  portalCustomization: PortalCustomizationSettings
) => {
  const classes: string[] = [];
  switch (portalCustomization.headerTextAlignment) {
    case HorizontalAlignmentOptions.LEFT:
      classes.push('self-start text-left');
      break;
    case HorizontalAlignmentOptions.CENTER:
      classes.push('self-center text-center');
      break;
    case HorizontalAlignmentOptions.RIGHT:
      classes.push('self-end text-end');
  }
  return classes.join(' ');
};

const getHeaderTextAlignmentContainerClass = (
  portalCustomization: PortalCustomizationSettings
) => {
  const classes: string[] = [];
  switch (portalCustomization.headerTextAlignment) {
    case HorizontalAlignmentOptions.LEFT:
      classes.push('text-left');
      break;
    case HorizontalAlignmentOptions.CENTER:
      classes.push('text-center');
      break;
    case HorizontalAlignmentOptions.RIGHT:
      classes.push('text-end');
  }
  return classes.join(' ');
};

const getBorderRadiusForOption = (
  borderRadiusOption: BorderRadiusOptions
): {
  sm: string;
  md: string;
  lg: string;
} | null => {
  if (!BORDER_RADIUS_CUSTOMIZATION_CONFIG[borderRadiusOption]) {
    return null;
  }
  switch (borderRadiusOption) {
    case BorderRadiusOptions.NONE:
      return {
        sm: '--radii-none',
        md: '--radii-none',
        lg: '--radii-none'
      };
    case BorderRadiusOptions.SM:
      return {
        sm: '--radii-xs',
        md: '--radii-sm',
        lg: '--radii-md'
      };
    case BorderRadiusOptions.MD:
      return {
        sm: '--radii-sm',
        md: '--radii-md',
        lg: '--radii-lg'
      };
    case BorderRadiusOptions.LG:
      return {
        sm: '--radii-lg',
        md: '--radii-2xl',
        lg: '--radii-3xl'
      };
  }
  return null;
};

const mapCustomizationSettingsToFormValues = (
  customizationSettings: CustomizationSettings
): Record<string, any> => {
  return cloneObject({
    ...customizationSettings.portalCustomization,
    ...customizationSettings.applicationCustomization
  });
};

const mapFormValuesToPortalCustomizationSettings = (
  data: Record<string, any>
): CustomizationSettings => {
  const {
    logoUrl,
    iconUrl,
    buttonColor,
    textColor,
    loginBgColor,
    disallowDisableEnabledConnection,
    logoPosition,
    logoAlignment,
    headerTextAlignment,
    socialPlacement,
    borderRadius,
    pageBackgroundCss,
    name,
    url,
    supportEmail,
    termsOfServiceUrl,
    privacyPolicyUrl
  } = data;

  return {
    portalCustomization: {
      ...(logoUrl !== undefined && { logoUrl }),
      ...(iconUrl !== undefined && { iconUrl }),
      ...(buttonColor && { buttonColor }),
      ...(buttonColor && { buttonHoverColor: getHoverColor(buttonColor, 0.2) }),
      ...(textColor && { textColor }),
      ...(loginBgColor && { loginBgColor }),
      ...(disallowDisableEnabledConnection && {
        disallowDisableEnabledConnection
      }),
      ...(logoPosition && { logoPosition }),
      ...(logoAlignment && { logoAlignment }),
      ...(headerTextAlignment && { headerTextAlignment }),
      ...(socialPlacement && { socialPlacement }),
      ...(borderRadius && { borderRadius }),
      ...(pageBackgroundCss && { pageBackgroundCss })
    },
    applicationCustomization: {
      ...(name && { name }),
      ...(url && { url }),
      ...(supportEmail && { supportEmail }),
      ...(termsOfServiceUrl && { termsOfServiceUrl }),
      ...(privacyPolicyUrl && { privacyPolicyUrl })
    }
  } as CustomizationSettings;
};

export {
  getTranslationKey,
  DEFAULT_COLORS,
  BORDER_RADIUS_CUSTOMIZATION_CONFIG,
  CUSTOM_CSS_DEFAULT_VALUE,
  getHoverColor,
  handleUpdateCustomizationError,
  getDefaultPortalCustomizationSettings,
  getDefaultApplicationCustomizationSettings,
  getCustomizationSettingsWithDefaults,
  CustomizationTabTypes,
  CustomizationWorkflowTypes,
  CustomizationPageTypes,
  CUSTOMIZATION_PAGE_TYPE_DISPLAY_SETTINGS,
  CUSTOMIZATION_WORKFLOW_TYPE_CONFIG,
  getWorkflowDisplaySettings,
  getPreviewUrl,
  getLogoContainerClass,
  getHeaderTextContainerClass,
  getHeaderTextAlignmentContainerClass,
  getBorderRadiusForOption,
  mapCustomizationSettingsToFormValues,
  mapFormValuesToPortalCustomizationSettings
};
